import React, { useState, useRef } from 'react';
import QRCodeModal from './QRCodeModal';
import TagManager from './TagManager';
import { 
  toggleFavorite, 
  updateOrderNote, 
  exportHistory, 
  importHistory,
  searchHistory,
  updateOrderTags,
  filterByTags,
  getAllTags
} from '../services/storage';
import { getTagById, getTagColor, getTagIcon, getTagName } from '../data/predefinedTags';

const RecoveryHistory = ({ history, onSelectOrder, onClearHistory, onUpdateHistory }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'favorites', or 'tag:tagId'
  const [showImportSuccess, setShowImportSuccess] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [currentEditingOrder, setCurrentEditingOrder] = useState(null);
  const fileInputRef = useRef(null);
  
  // Получаем все уникальные теги из истории
  const allUsedTags = getAllTags();

  if (!history || history.length === 0) {
    return null;
  }

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const showQRCodeForItem = (item) => {
    setSelectedItem(item);
    setShowQRCode(true);
  };

  const handleToggleFavorite = (e, orderNumber) => {
    e.stopPropagation();
    const updatedHistory = toggleFavorite(orderNumber);
    onUpdateHistory(updatedHistory);
  };

  const handleStartEditNote = (e, item) => {
    e.stopPropagation();
    setEditingNoteId(item.id);
    setNoteText(item.note || '');
  };

  const handleSaveNote = (e, orderNumber) => {
    e.stopPropagation();
    const updatedHistory = updateOrderNote(orderNumber, noteText);
    onUpdateHistory(updatedHistory);
    setEditingNoteId(null);
    setNoteText('');
  };

  const handleCancelEditNote = (e) => {
    e.stopPropagation();
    setEditingNoteId(null);
    setNoteText('');
  };

  const handleExport = () => {
    const success = exportHistory();
    if (success) {
      // Можно показать уведомление об успехе
      console.log('История успешно экспортирована');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const updatedHistory = await importHistory(file);
      onUpdateHistory(updatedHistory);
      setShowImportSuccess(true);
      setTimeout(() => setShowImportSuccess(false), 3000);
    } catch (error) {
      alert(error.message);
    }

    // Сбрасываем input
    e.target.value = '';
  };

  const handleOpenTagManager = (e, item) => {
    e.stopPropagation();
    setCurrentEditingOrder(item);
    setShowTagManager(true);
  };

  const handleTagsChange = (tags) => {
    if (currentEditingOrder) {
      const updatedHistory = updateOrderTags(currentEditingOrder.orderNumber, tags);
      onUpdateHistory(updatedHistory);
      setCurrentEditingOrder(null);
    }
  };

  // Фильтрация истории
  let filteredHistory = searchQuery ? searchHistory(searchQuery) : history;
  if (filter === 'favorites') {
    filteredHistory = filteredHistory.filter(item => item.favorite);
  } else if (filter.startsWith('tag:')) {
    const tagId = filter.substring(4);
    filteredHistory = filteredHistory.filter(item => item.tags && item.tags.includes(tagId));
  }

  return (
    <div className="card p-4 md:p-6 mb-4 md:mb-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 flex items-center">
          <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          История восстановлений
        </h3>
        <div className="flex items-center gap-2">
          {/* Export/Import кнопки */}
          <button
            onClick={handleExport}
            className="flex items-center px-2 py-1 text-xs md:text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
            title="Экспортировать историю"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
          <button
            onClick={handleImportClick}
            className="flex items-center px-2 py-1 text-xs md:text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
            title="Импортировать историю"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImportFile}
            className="hidden"
          />
          <button
            onClick={onClearHistory}
            className="flex items-center px-2 py-1 text-xs md:text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            title="Очистить историю"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Уведомление об успешном импорте */}
      {showImportSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center animate-fade-in">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm text-green-800">История успешно импортирована!</span>
        </div>
      )}

      {/* Поиск */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Поиск по номеру заказа или заметкам..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
          />
          <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Фильтры */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            filter === 'all' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Все ({history.length})
        </button>
        <button
          onClick={() => setFilter('favorites')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center ${
            filter === 'favorites' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <svg className="w-4 h-4 mr-1" fill={filter === 'favorites' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
          Избранное ({history.filter(item => item.favorite).length})
        </button>
        
        {/* Фильтры по тегам */}
        {allUsedTags.length > 0 && allUsedTags.map(tagId => {
          const tag = getTagById(tagId);
          if (!tag) return null;
          const taggedCount = history.filter(item => item.tags && item.tags.includes(tagId)).length;
          if (taggedCount === 0) return null;
          
          return (
            <button
              key={tagId}
              onClick={() => setFilter(filter === `tag:${tagId}` ? 'all' : `tag:${tagId}`)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center ${
                filter === `tag:${tagId}`
                  ? 'text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{
                backgroundColor: filter === `tag:${tagId}` ? tag.color : undefined
              }}
            >
              <span className="mr-1">{tag.icon}</span>
              {tag.name} ({taggedCount})
            </button>
          );
        })}
      </div>

      {/* Telegram Bot Ad */}
      <div className="mb-4 p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-white mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
            <div>
              <p className="text-sm font-medium text-white">
                🚀 @vless_helperz_bot
              </p>
              <p className="text-xs text-blue-100">
                Статистика, бонусы, промокоды
              </p>
            </div>
          </div>
          <a
            href="https://t.me/vless_helperz_bot"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 bg-white text-blue-600 rounded-md text-xs font-medium hover:bg-blue-50 transition-colors"
          >
            Привязать
          </a>
        </div>
      </div>

      <div className="space-y-2 md:space-y-3 max-h-96 overflow-y-auto">
        {filteredHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p>Ничего не найдено</p>
          </div>
        ) : (
          filteredHistory.map((item, index) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all duration-200"
            >
              <div
                className="p-3 md:p-4 cursor-pointer flex items-center justify-between"
                onClick={() => toggleExpand(item.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex items-center gap-2">
                      {/* Кнопка избранного */}
                      <button
                        onClick={(e) => handleToggleFavorite(e, item.orderNumber)}
                        className="flex-shrink-0 text-yellow-500 hover:text-yellow-600 transition-colors"
                        title={item.favorite ? 'Убрать из избранного' : 'Добавить в избранное'}
                      >
                        <svg className="w-5 h-5" fill={item.favorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                      <p className="text-sm md:text-base font-medium text-gray-900 truncate">
                        Заказ: {item.orderNumber}
                      </p>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 w-fit">
                      {item.status}
                    </span>
                  </div>
                  {item.note && (
                    <p className="text-xs text-blue-600 mt-1 italic truncate">
                      📝 {item.note}
                    </p>
                  )}
                  {/* Теги */}
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.tags.map(tagId => {
                        const tag = getTagById(tagId);
                        if (!tag) return null;
                        return (
                          <span
                            key={tagId}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: `${tag.color}20`,
                              color: tag.color,
                              border: `1px solid ${tag.color}40`
                            }}
                          >
                            <span className="mr-0.5">{tag.icon}</span>
                            {tag.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs md:text-sm text-gray-500 mt-1">
                    {new Date(item.timestamp).toLocaleString('ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${
                    expandedId === item.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

            {expandedId === item.id && (
              <div className="px-3 md:px-4 pb-3 md:pb-4 border-t border-gray-100 pt-3 md:pt-4 animate-slide-up">
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-xs md:text-sm text-gray-600 mb-2 font-medium">Подписка:</p>
                  <p className="text-xs md:text-sm font-mono text-gray-800 break-all leading-relaxed">
                    {item.subscription}
                  </p>
                </div>

                {/* Секция тегов */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 mb-3 border border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs md:text-sm text-gray-700 font-medium flex items-center">
                      <svg className="w-4 h-4 mr-1 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Теги
                    </p>
                    <button
                      onClick={(e) => handleOpenTagManager(e, item)}
                      className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                    >
                      {item.tags && item.tags.length > 0 ? 'Изменить' : 'Добавить'}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {item.tags && item.tags.length > 0 ? (
                      item.tags.map(tagId => {
                        const tag = getTagById(tagId);
                        if (!tag) return null;
                        return (
                          <span
                            key={tagId}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: `${tag.color}20`,
                              color: tag.color,
                              border: `2px solid ${tag.color}`
                            }}
                          >
                            <span className="mr-1">{tag.icon}</span>
                            {tag.name}
                          </span>
                        );
                      })
                    ) : (
                      <p className="text-xs text-gray-500 italic">Нет тегов</p>
                    )}
                  </div>
                </div>

                {/* Секция заметок */}
                <div className="bg-blue-50 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs md:text-sm text-gray-700 font-medium flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Заметка
                    </p>
                    {editingNoteId !== item.id && (
                      <button
                        onClick={(e) => handleStartEditNote(e, item)}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        {item.note ? 'Изменить' : 'Добавить'}
                      </button>
                    )}
                  </div>
                  {editingNoteId === item.id ? (
                    <div>
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Введите заметку..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                        rows="2"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={(e) => handleSaveNote(e, item.orderNumber)}
                          className="text-xs bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-3 rounded transition-colors"
                        >
                          Сохранить
                        </button>
                        <button
                          onClick={handleCancelEditNote}
                          className="text-xs bg-gray-300 hover:bg-gray-400 text-gray-700 py-1.5 px-3 rounded transition-colors"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs md:text-sm text-gray-600 italic">
                      {item.note || 'Нет заметки'}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(item.subscription);
                    }}
                    className="text-xs md:text-sm bg-primary-600 hover:bg-primary-700 text-white py-2.5 px-3 rounded-lg transition-colors font-medium"
                  >
                    Копировать
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      showQRCodeForItem(item);
                    }}
                    className="text-xs md:text-sm bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-3 rounded-lg transition-colors font-medium"
                  >
                    QR код
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectOrder(item);
                    }}
                    className="text-xs md:text-sm bg-gray-600 hover:bg-gray-700 text-white py-2.5 px-3 rounded-lg transition-colors font-medium"
                  >
                    Детали
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
        )}
      </div>
      
      <QRCodeModal 
        isOpen={showQRCode}
        onClose={() => setShowQRCode(false)}
        subscription={selectedItem?.subscription}
        orderNumber={selectedItem?.orderNumber}
      />
      
      <TagManager
        currentTags={currentEditingOrder?.tags || []}
        onTagsChange={handleTagsChange}
        onClose={() => {
          setShowTagManager(false);
          setCurrentEditingOrder(null);
        }}
        isOpen={showTagManager}
      />
    </div>
  );
};

export default RecoveryHistory;
